// Exporta una cotización a Excel (.xlsx) en el navegador, con la misma información que el PDF.
// Los totales quedan como fórmulas (la cantidad, el precio o el descuento se pueden retocar en Excel y
// todo se recalcula con la misma regla que el sistema: total de línea = round(cantidad × precio × (1 − desc/100)),
// IVA = round(neto × 0,19)).
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const FMT_MONEDA = '"$"#,##0';
const FMT_PCT = '0.##"%"';

// La fecha de la cotización es un día, no un instante: "2026-10-20T00:00:00.000Z" no se pasa por Date, porque en
// Chile (UTC−3/−4) se mostraba el día anterior.
function fmtFecha(fecha) {
  if (!fecha) return '';
  const m = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return String(fecha);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function generarExcelCotizacion(cotizacion, config) {
  const incluyeAutor = cotizacion.incluye_autor !== false;
  const incluyeEditorial = cotizacion.incluye_editorial !== false;
  const incluyeDescuento = cotizacion.incluye_descuento !== false;
  const lineas = cotizacion.lineas || [];
  // Con el descuento oculto pero distinto de cero el total de línea no se puede reconstruir desde las columnas visibles
  const usarFormulas = incluyeDescuento || lineas.every(l => !(Number(l.descuento_pct) > 0));

  // Columnas de la tabla, en orden
  const cols = [{ clave: 'isbn', titulo: 'ISBN', ancho: 16 }, { clave: 'titulo', titulo: 'Título', ancho: 46 }];
  if (incluyeAutor) cols.push({ clave: 'autor', titulo: 'Autor', ancho: 24 });
  if (incluyeEditorial) cols.push({ clave: 'editorial', titulo: 'Editorial', ancho: 22 });
  cols.push({ clave: 'cantidad', titulo: 'Cantidad', ancho: 10 }, { clave: 'precio', titulo: 'Precio Neto', ancho: 14 });
  if (incluyeDescuento) cols.push({ clave: 'desc', titulo: 'DESC%', ancho: 9 });
  cols.push({ clave: 'total', titulo: 'Total', ancho: 14 });
  const idx = Object.fromEntries(cols.map((c, i) => [c.clave, i]));
  const letra = clave => XLSX.utils.encode_col(idx[clave]);
  const ultima = cols.length - 1;

  const filas = []; // cada fila: array de celdas (valor o {t, v, f, z})
  const celda = (valor, extra = {}) => ({ v: valor, t: typeof valor === 'number' ? 'n' : 's', ...extra });

  filas.push([celda(`COTIZACIÓN N° ${cotizacion.numero}`)]);
  filas.push([]);

  const empresa = [
    config?.nombre_empresa,
    config?.rut,
    config?.giro,
    config?.direccion ? `${config.direccion}${config?.comuna ? ', ' + config.comuna : ''}` : (config?.comuna || null),
    config?.telefono,
    config?.correo,
  ].filter(Boolean);
  const cliente = [
    ['Cliente', cotizacion.cliente_nombre],
    ['RUT', cotizacion.cliente_rut],
    ['Giro', cotizacion.cliente_giro],
    ['Dirección', cotizacion.cliente_direccion ? `${cotizacion.cliente_direccion}${cotizacion.cliente_comuna ? ', ' + cotizacion.cliente_comuna : ''}` : (cotizacion.cliente_comuna || null)],
    ['Correo', cotizacion.cliente_correo],
    ['Fecha', fmtFecha(cotizacion.fecha || cotizacion.creado_en)],
  ].filter(([, v]) => v);
  empresa.forEach(t => filas.push([celda(String(t))]));
  if (empresa.length) filas.push([]);
  cliente.forEach(([k, v]) => filas.push([celda(k), celda(String(v))]));
  filas.push([]);

  filas.push(cols.map(c => celda(c.titulo)));

  const primeraLinea = filas.length; // índice (0-based) de la primera línea de libros
  lineas.forEach((l, i) => {
    const r = primeraLinea + i + 1; // número de fila en Excel (1-based)
    const fila = new Array(cols.length);
    fila[idx.isbn] = celda(l.isbn || '');
    fila[idx.titulo] = celda(l.titulo || '');
    if (incluyeAutor) fila[idx.autor] = celda(l.autor || '');
    if (incluyeEditorial) fila[idx.editorial] = celda(l.editorial || '');
    fila[idx.cantidad] = celda(Number(l.cantidad) || 0, { z: '0' });
    fila[idx.precio] = celda(Number(l.precio_neto) || 0, { z: FMT_MONEDA });
    if (incluyeDescuento) fila[idx.desc] = celda(Number(l.descuento_pct) || 0, { z: FMT_PCT });
    const total = Number(l.total_linea) || 0;
    fila[idx.total] = usarFormulas
      ? celda(total, { z: FMT_MONEDA, f: `ROUND(${letra('cantidad')}${r}*${letra('precio')}${r}*(1-${incluyeDescuento ? `${letra('desc')}${r}` : '0'}/100),0)` })
      : celda(total, { z: FMT_MONEDA });
    filas.push(fila);
  });
  const ultimaLinea = primeraLinea + lineas.length - 1; // 0-based

  // Totales, alineados bajo la columna Total (etiqueta en la columna anterior)
  filas.push([]);
  const rangoTotal = lineas.length > 0 ? `${letra('total')}${primeraLinea + 1}:${letra('total')}${ultimaLinea + 1}` : null;
  const filaNeto = filas.length + 1, filaIva = filaNeto + 1; // números de fila de Excel
  const filaTot = (etiqueta, valor, f) => {
    const fila = new Array(cols.length);
    fila[ultima - 1] = celda(etiqueta);
    fila[ultima] = celda(Number(valor) || 0, { z: FMT_MONEDA, ...(f ? { f } : {}) });
    return fila;
  };
  filas.push(filaTot('Monto Neto', cotizacion.mnt_neto, usarFormulas && rangoTotal ? `SUM(${rangoTotal})` : null));
  filas.push(filaTot('IVA 19%', cotizacion.mnt_iva, usarFormulas && rangoTotal ? `ROUND(${letra('total')}${filaNeto}*0.19,0)` : null));
  filas.push(filaTot('Total', cotizacion.mnt_total, usarFormulas && rangoTotal ? `${letra('total')}${filaNeto}+${letra('total')}${filaIva}` : null));

  filas.push([]);
  filas.push([celda('Validez de la cotización: 30 días desde la fecha de emisión.')]);
  if (cotizacion.notas) filas.push([celda(String(cotizacion.notas))]);
  if (cotizacion.datos_deposito) {
    filas.push([]);
    filas.push([celda('Depositar:')]);
    String(cotizacion.datos_deposito).split(/\r?\n/).forEach(t => filas.push([celda(t)]));
  }

  const ws = {};
  let maxCol = 0;
  filas.forEach((fila, r) => {
    fila.forEach((c, k) => {
      if (!c) return;
      ws[XLSX.utils.encode_cell({ r, c: k })] = c;
      if (k > maxCol) maxCol = k;
    });
  });
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: filas.length - 1, c: Math.max(maxCol, ultima) } });
  ws['!cols'] = cols.map(c => ({ wch: c.ancho }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Cotización ${cotizacion.numero}`.slice(0, 31));
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Cotizacion-${cotizacion.numero}.xlsx`);
}
